import { useRef } from 'react';
import MobileModalHeader from '@shared/components/atoms/MobileModalHeader';

import searchIcon from '@shared/assets/images/search_icon.svg';
import closeIcon from '@shared/assets/images/x-close-b.svg';

import useMobileRecipientSelector from './hooks/useMobileRecipientSelector';
import styles from './MobileRecipientSelector.module.scss';

/**
 * MobileRecipientSelector
 * 모바일용 참조자 선택 컴포넌트 (전체 화면 모달)
 *
 * Step 1: 직원 선택 화면 (부서 트리 + 직원 테이블)
 * Step 2: 선택 확인 화면 (선택된 직원 목록)
 *
 * @param {boolean} isOpen - 모달 열림 여부
 * @param {function} onClose - 모달 닫기 콜백
 * @param {function} onSelect - 선택 완료 콜백 (selectedEmployees 배열 전달)
 * @param {array} selectedRecipients - 기존 선택된 참조자 목록
 */
export default function MobileRecipientSelector({
  isOpen,
  onClose,
  onSelect,
  selectedRecipients = [],
}) {
  const employeeSectionRef = useRef(null);
  const {
    // 상태
    step,
    searchName,
    selectedEmployees,
    expandedTeams,
    selectedTeamCode,
    showSearchResults,
    displayEmployees,
    teamList,
    tree,

    // 핸들러
    toggleTeam,
    handleTeamSelect,
    handleEmployeeToggle,
    handleRemoveEmployee,
    handleSearch,
    handleSearchNameChange,
    handleNext,
    handleBack,
    handleComplete,
    handleSelectAll,

    // 유틸리티
    isEmployeeSelected,
    isAllSelected,
  } = useMobileRecipientSelector({
    isOpen,
    onClose,
    onSelect,
    selectedRecipients,
  });

  /**
   * 트리 노드 렌더링 (PC 버전과 동일한 연결 라인 구조)
   * @param {number} teamCode - 팀 코드
   * @param {number} depth - 깊이
   * @param {boolean} isLast - 현재 레벨에서 마지막 노드인지
   * @param {boolean[]} ancestorLastFlags - 상위 노드들의 isLast 플래그 배열
   */
  function renderTreeNode(teamCode, depth = 0, isLast = true, ancestorLastFlags = []) {
    const item = tree.byCode.get(teamCode);
    const children = tree.childrenMap.get(teamCode) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedTeams.has(teamCode);
    const isActive = selectedTeamCode === teamCode;
    const label = item?.team || item?.name || '';

    return (
      <li key={teamCode} className={styles.treeNode}>
        <div className={styles.nodeRow}>
          {/* 연결 라인 가이드 */}
          <div className={styles.indentGuides}>
            {ancestorLastFlags.map(function (ancestorIsLast, idx) {
              return (
                <span
                  key={`guide-${teamCode}-${idx}`}
                  className={styles.guide}
                  data-line={ancestorIsLast ? 'off' : 'on'}
                />
              );
            })}
            <span className={styles.branch} data-last={isLast ? 'true' : 'false'}>
              {hasChildren ? (
                <button
                  type="button"
                  className={styles.toggleBtn}
                  onClick={function () {
                    toggleTeam(teamCode);
                  }}
                >
                  <span
                    className={`${styles.toggleIcon} ${isExpanded ? styles.toggleIconExpanded : styles.toggleIconCollapsed}`}
                  />
                </button>
              ) : (
                <span className={styles.toggleSpacer} />
              )}
            </span>
          </div>

          <button
            type="button"
            className={`${styles.teamName} ${isActive ? styles.teamNameActive : ''}`}
            onClick={function () {
              handleTeamSelect(teamCode);

              // 포커스 이동
              requestAnimationFrame(() => {
                employeeSectionRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              });
            }}
          >
            {label}
          </button>
        </div>

        {hasChildren && isExpanded && (
          <ul className={styles.treeChildren}>
            {children.map(function (childCode, index) {
              return renderTreeNode(childCode, depth + 1, index === children.length - 1, [
                ...ancestorLastFlags,
                isLast,
              ]);
            })}
          </ul>
        )}
      </li>
    );
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.page}>
      <MobileModalHeader title="참조자 선택" onBack={handleBack} />

      {step === 1 && (
        <div className={styles.stepOne}>
          <div className={styles.mainContent}>
            {/* 부서 트리 (PC 버전과 동일한 구조) */}
            <div className={styles.treeSection}>
              <div className={styles.companyName}>{teamList?.[0]?.company || '링크아이'}</div>
              <ul className={styles.treeList}>
                {tree.roots.map(function (code, index) {
                  return renderTreeNode(code, 0, index === tree.roots.length - 1, []);
                })}
              </ul>
            </div>

            {/* 오른쪽: 검색 + 직원 테이블 */}
            <div className={styles.employeeSection} ref={employeeSectionRef}>
              {/* 검색 필드 */}
              <div className={styles.searchField}>
                <img src={searchIcon} alt="검색" className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="검색할 이름을 입력하세요."
                  value={searchName}
                  onChange={function (e) {
                    handleSearchNameChange(e.target.value);
                  }}
                  onKeyDown={function (e) {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  className={styles.searchInput}
                />
              </div>

              {/* 직원 테이블 - 피그마 기준 체크박스 포함 */}
              <div className={styles.employeeTable}>
                {/* 헤더 */}
                <div className={styles.tableHeader}>
                  <div
                    className={styles.colCheck}
                    onClick={function () {
                      if (displayEmployees.length > 0) {
                        handleSelectAll();
                      }
                    }}
                  >
                    <span
                      className={`${styles.checkbox} ${isAllSelected() && displayEmployees.length > 0 ? styles.checkboxChecked : ''}`}
                    >
                      {isAllSelected() && displayEmployees.length > 0 && (
                        <span className={styles.checkmark} />
                      )}
                    </span>
                  </div>
                  <div className={styles.colDept}>부서</div>
                  <div className={styles.colPosition}>직급</div>
                  <div className={styles.colName}>이름</div>
                </div>

                {/* 바디 */}
                <div className={styles.tableBody}>
                  {displayEmployees.length > 0 ? (
                    displayEmployees.map(function (employee) {
                      const selected = isEmployeeSelected(employee);
                      return (
                        <div
                          key={employee.enumber}
                          className={styles.tableRow}
                          onClick={function () {
                            handleEmployeeToggle(employee);
                          }}
                        >
                          <div className={styles.colCheck}>
                            <span
                              className={`${styles.checkbox} ${selected ? styles.checkboxChecked : ''}`}
                            >
                              {selected && <span className={styles.checkmark} />}
                            </span>
                          </div>
                          <div className={styles.colDept}>{employee.team || '-'}</div>
                          <div className={styles.colPosition}>{employee.position || '-'}</div>
                          <div className={styles.colName}>{employee.name}</div>
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.emptyRow}>
                      {showSearchResults ? '검색 결과가 없습니다.' : '부서를 선택하세요.'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 하단 버튼 - 피그마 기준 그라데이션 + 버튼 */}
          <div className={styles.footer}>
            <div className={styles.footerGradient} />
            <div className={styles.footerButtons}>
              <button
                type="button"
                className={styles.nextButton}
                onClick={handleNext}
                disabled={selectedEmployees.length === 0}
              >
                다음({selectedEmployees.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className={styles.stepTwo}>
          <div className={styles.selectedListContainer}>
            {/* 피그마 기준: #FAFAFA 배경의 wrapper */}
            <div className={styles.selectedListWrapper}>
              <div className={styles.selectedList}>
                {selectedEmployees.map(function (employee, index) {
                  return (
                    <div key={`${employee.enumber}-${index}`} className={styles.selectedItem}>
                      <span className={styles.itemIndex}>{index + 1}</span>
                      <span className={styles.itemName}>{employee.name}</span>
                      <span className={styles.itemTeam}>{employee.team || '-'}</span>
                      <button
                        type="button"
                        className={styles.removeBtn}
                        onClick={function () {
                          handleRemoveEmployee(index);
                        }}
                      >
                        <img src={closeIcon} alt="삭제" className={styles.removeIcon} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 하단 버튼 - 피그마 기준 그라데이션 + 버튼 */}
          <div className={styles.footer}>
            <div className={styles.footerGradient} />
            <div className={styles.footerButtons}>
              <button type="button" className={styles.completeButton} onClick={handleComplete}>
                선택 완료
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
